import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  Button,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

const TermsOfService: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ py: { xs: 4, md: 8 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="md">
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>
        <Paper sx={{ p: { xs: 3, md: 6 } }}>
          <Typography variant="h3" component="h1" gutterBottom>
            Terms of Service
          </Typography>
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Last updated: {new Date().toLocaleDateString()}
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" gutterBottom>
            1. Acceptance of Terms
          </Typography>
          <Typography paragraph>
            By accessing and using TNCOSOFT's website and services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </Typography>

          <Typography variant="h5" gutterBottom>
            2. Services Description
          </Typography>
          <Typography paragraph>
            TNCOSOFT provides professional IT infrastructure services including:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <Typography component="li" paragraph>Infrastructure assessments and capacity planning</Typography>
            <Typography component="li" paragraph>Virtualization platform implementation and optimization</Typography>
            <Typography component="li" paragraph>Private cloud infrastructure design and deployment</Typography>
            <Typography component="li" paragraph>Disaster recovery planning and backup solutions</Typography>
            <Typography component="li" paragraph>Consumption-based IT infrastructure (as-a-Service models)</Typography>
          </Box>

          <Typography variant="h5" gutterBottom>
            3. Service Agreements
          </Typography>
          <Typography paragraph>
            Specific IT infrastructure services will be governed by separate service agreements that detail scope, deliverables, timelines, and pricing. These terms serve as the general framework for all TNCOSOFT services.
          </Typography>

          <Typography variant="h5" gutterBottom>
            4. Client Responsibilities
          </Typography>
          <Typography paragraph>
            Clients agree to:
          </Typography>
          <Box component="ul" sx={{ pl: 3, mb: 3 }}>
            <Typography component="li" paragraph>Provide accurate and complete information about existing infrastructure</Typography>
            <Typography component="li" paragraph>Grant necessary access to infrastructure environments and systems</Typography>
            <Typography component="li" paragraph>Implement recommended infrastructure improvements in a timely manner</Typography>
            <Typography component="li" paragraph>Maintain confidentiality of assessment findings and proprietary methodologies</Typography>
            <Typography component="li" paragraph>Pay fees according to agreed terms</Typography>
          </Box>

          <Typography variant="h5" gutterBottom>
            5. Confidentiality and Data Protection
          </Typography>
          <Typography paragraph>
            TNCOSOFT maintains strict confidentiality regarding all client information, infrastructure configurations, and assessment findings. We implement industry-standard data protection measures and comply with applicable privacy regulations.
          </Typography>

          <Typography variant="h5" gutterBottom>
            6. Limitation of Liability
          </Typography>
          <Typography paragraph>
            While TNCOSOFT strives to provide the highest quality IT infrastructure services, we cannot guarantee 100% uptime or immunity from all technical issues. Our liability is limited to the fees paid for specific services, except in cases of gross negligence or willful misconduct.
          </Typography>

          <Typography variant="h5" gutterBottom>
            7. Service Level Agreements
          </Typography>
          <Typography paragraph>
            Response times and service levels are defined in individual service agreements. Critical infrastructure incidents and system outages receive priority response according to agreed SLA terms.
          </Typography>
          <Typography paragraph>
            <strong>Uptime Guarantee:</strong> Our 99.999% uptime guarantee applies specifically to infrastructure under active maintenance agreements with proper redundancy configurations. Actual uptime may vary based on factors including configuration, scheduled maintenance windows, and external dependencies.
          </Typography>

          <Typography variant="h5" gutterBottom>
            8. Intellectual Property
          </Typography>
          <Typography paragraph>
            All methodologies, tools, and documentation developed by TNCOSOFT remain our intellectual property. Clients receive usage rights for delivered services but not ownership of underlying intellectual property.
          </Typography>

          <Typography variant="h5" gutterBottom>
            9. Third-Party Services
          </Typography>
          <Typography paragraph>
            TNCOSOFT may utilize third-party infrastructure tools and services from our technology partners. Use of such services is subject to their respective terms and conditions.
          </Typography>

          <Typography variant="h5" gutterBottom>
            10. Payment Terms
          </Typography>
          <Typography paragraph>
            Payment terms are specified in individual service agreements. Generally, payments are due within 30 days of invoice date. Late payments may result in service suspension and additional fees.
          </Typography>

          <Typography variant="h5" gutterBottom>
            11. Termination
          </Typography>
          <Typography paragraph>
            Either party may terminate services with appropriate notice as specified in service agreements. TNCOSOFT reserves the right to terminate services immediately for non-payment or breach of terms.
          </Typography>

          <Typography variant="h5" gutterBottom>
            12. Force Majeure
          </Typography>
          <Typography paragraph>
            TNCOSOFT shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including but not limited to acts of God, natural disasters, power outages, network failures, or government actions.
          </Typography>

          <Typography variant="h5" gutterBottom>
            13. Governing Law
          </Typography>
          <Typography paragraph>
            These terms shall be governed by and construed in accordance with applicable laws. Any disputes shall be resolved through appropriate legal channels.
          </Typography>

          <Typography variant="h5" gutterBottom>
            14. Professional Standards
          </Typography>
          <Typography paragraph>
            TNCOSOFT adheres to industry best practices and professional standards for IT infrastructure services. Our team maintains relevant certifications from leading technology vendors and follows established engineering principles and ethical guidelines.
          </Typography>

          <Typography variant="h5" gutterBottom>
            15. Modifications
          </Typography>
          <Typography paragraph>
            TNCOSOFT reserves the right to modify these terms at any time. Material changes will be communicated to clients with appropriate notice.
          </Typography>

          <Typography variant="h5" gutterBottom>
            16. Contact Information
          </Typography>
          <Typography paragraph>
            For questions regarding these terms of service, please contact:
          </Typography>
          <Box sx={{ pl: 2, mb: 3 }}>
            <Typography paragraph><strong>TNCOSOFT</strong></Typography>
            <Typography paragraph>Email: njinabad@tncosoft.com</Typography>
            <Typography paragraph>Phone: +91-8999096807</Typography>
          </Box>

          <Divider sx={{ my: 3 }} />
          
          <Typography variant="body2" color="text.secondary">
            These terms of service are effective as of {new Date().toLocaleDateString()} and constitute the entire agreement between TNCOSOFT and its clients regarding the use of our services.
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
};

export default TermsOfService;